import React from 'react';

import ScreenVoteButton from '../ScreenVoteButton';
import ChangeVoteButton from '../ChangeVoteButton';
import ResolveVoteInfo from '../ResolveVoteInfo';
import ResolveVoteButton from '../ResolveVoteButton';
import { Text } from '../../../../../components/core';
import styles from './ScreeningVotePanel.module.scss';

import { VoteContainer } from './VoteContainer';
import { AsyncVoteFunction, useMakeVote } from 'hooks/study/useMakeVote';
import { Filters, VoteDecision } from 'pages/StudyList/StudyList';
import { Inline } from 'components/layout';
import { usePrevious } from 'hooks/usePrevious';

type VotePanelProps = {
  currentFilter: Filters;
  handleScreeningVote: AsyncVoteFunction;
  currentVote: VoteDecision;
  otherVoters?: (string | undefined)[];
  canResolveConflicts?: boolean;
};

export const ScreeningVotePanel = ({
  currentFilter,
  handleScreeningVote,
  currentVote,
  otherVoters,
  canResolveConflicts = true,
}: VotePanelProps) => {
  const previousFilter = usePrevious(currentFilter);
  const isPersistent = currentFilter === 'awaitingOtherReviewer' ? true : false;
  const [makeVote, isVoting] = useMakeVote(handleScreeningVote, isPersistent);
  const activeFilter =
    isVoting && previousFilter ? previousFilter : currentFilter;

  if (activeFilter === 'screening') {
    return (
      <VoteContainer>
        <ScreenVoteButton vote="No" doVote={makeVote} isVoting={isVoting} />
        <ScreenVoteButton vote="Maybe" doVote={makeVote} isVoting={isVoting} />
        <ScreenVoteButton vote="Yes" doVote={makeVote} isVoting={isVoting} />
      </VoteContainer>
    );
  }

  if (activeFilter === 'awaitingOtherReviewer') {
    return (
      <VoteContainer>
        <ChangeVoteButton
          vote="No"
          currentVote={currentVote}
          doVote={makeVote}
          isVoting={isVoting}
        />
        <ChangeVoteButton
          vote="Maybe"
          currentVote={currentVote}
          doVote={makeVote}
          isVoting={isVoting}
        />
        <ChangeVoteButton
          vote="Yes"
          currentVote={currentVote}
          doVote={makeVote}
          isVoting={isVoting}
        />
      </VoteContainer>
    );
  }

  if (activeFilter === 'resolveConflicts') {
    return (
      <VoteContainer
        spacing={3}
        className={styles.resolveContainer}
        data-testid="resolve-conflicts-panel"
      >
        <ResolveVoteInfo
          name1={otherVoters ? otherVoters[0] : undefined}
          name2={otherVoters ? otherVoters[1] : undefined}
          showPrompt={canResolveConflicts}
        />
        {canResolveConflicts ? (
          <Inline alignX="spaceBetween" className={styles.buttonContainer}>
            <ResolveVoteButton
              vote="No"
              doVote={makeVote}
              isVoting={isVoting}
            />
            <ResolveVoteButton
              vote="Maybe"
              doVote={makeVote}
              isVoting={isVoting}
            />
            <ResolveVoteButton
              vote="Yes"
              doVote={makeVote}
              isVoting={isVoting}
            />
          </Inline>
        ) : (
          <Text>
            {"You currently don't have the permission to resolve conflicts."}
          </Text>
        )}
      </VoteContainer>
    );
  }

  return null;
};
